import { registrationConsent } from '../legal/registration';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { createGoogleProof } from '../components/AuthButtonGoogleCustom/googleOAuth';
import { appLocalStorage } from '../utils/storage';
import config from '../appLinks/config.json';
import { socialAttemptKey as key } from '../utils/authAttemptStorage';
export { abortable } from '../utils/abortable';

const native = registerPlugin('WiwSocialAuth');
export const providerNames = ['discord', 'tiktok'];
const opaque = value => typeof value === 'string' && /^[a-f0-9]{64}$/.test(value);
export const socialPlatform = () => Capacitor.getPlatform() === 'ios' || window.location.protocol === 'capacitor:' ? 'ios'
  : /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || navigator.maxTouchPoints > 1 ? 'web' : 'desktop';
export const createProof = createGoogleProof;
export const openNative = (url, state, provider) => native.authenticate({ url, state, provider });
export const cancelNative = state => native.cancel({ state }).catch(() => {});

export async function socialRequest(path, body, signal) {
  const response = await fetch(`${(process.env.REACT_APP_API_URL || '').replace(/\/$/, '')}/auth/social${path}`, {
    method: body ? 'POST' : 'GET', signal, credentials: 'omit', cache: 'no-store',
    ...(body ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) } : {}),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.error || 'AUTH_UNAVAILABLE');
  return data;
}

export function rememberAttempt(provider, state, verifier) {
  if (!providerNames.includes(provider) || !opaque(state) || !/^[A-Za-z0-9._~-]{43,128}$/.test(verifier || '')) throw Error('Invalid attempt');
  const value = JSON.stringify({ state, verifier, registration: registrationConsent(), createdAt: Date.now() });
  appLocalStorage.setItem(key(provider), value);
  if (appLocalStorage.getItem(key(provider)) !== value) throw Error('Storage unavailable');
}
export function consumeAttempt(provider, state) {
  try {
    if (!providerNames.includes(provider) || !opaque(state)) return null;
    const attempt = JSON.parse(appLocalStorage.getItem(key(provider)));
    if (!attempt || attempt.state !== state) return null;
    appLocalStorage.removeItem(key(provider));
    if (!Number.isFinite(attempt.createdAt) || Date.now() - attempt.createdAt < 0 || Date.now() - attempt.createdAt > 12 * 60000
      || !/^[A-Za-z0-9._~-]{43,128}$/.test(attempt.verifier || '')) return null;
    return attempt;
  } catch { return null; }
}
export function readCallback(address) {
  const url = new URL(address);
  const query = url.searchParams;
  if (!query.has('social_provider') && !query.has('social_error')) return null;
  if (query.has('social_error')) return { invalid: true };
  const keys = ['social_provider', 'social_state', 'social_code'];
  if (keys.some(key => query.getAll(key).length !== 1) || !providerNames.includes(query.get('social_provider'))
    || !opaque(query.get('social_state')) || !opaque(query.get('social_code'))) return { invalid: true };
  return { provider: query.get('social_provider'), state: query.get('social_state'), ticket: query.get('social_code') };
}
export function validNativeCallback(address, provider, state) {
  try {
    const url = new URL(address);
    const data = readCallback(address);
    return url.protocol === `${config.iosScheme}:` && url.hostname === new URL(config.origin).hostname
      && url.pathname === '/login' && !url.port && !url.username && !url.password && !url.hash
      && data && !data.invalid && data.provider === provider && data.state === state;
  } catch { return false; }
}

export function validAuthorization(address, provider, state) {
  try {
    const url = new URL(address);
    const targets = { discord: ['discord.com', /^\/oauth2\/authorize$/],
      tiktok: ['www.tiktok.com', /^\/v2\/auth\/authorize\/$/] };
    const target = targets[provider];
    const redirect = new URL(url.searchParams.get('redirect_uri'));
    return target && url.protocol === 'https:' && !url.port && !url.username && !url.password && url.hostname === target[0]
      && !url.hash
      && target[1].test(url.pathname) && url.searchParams.getAll('state').length === 1 && url.searchParams.get('state') === state
      && opaque(state) && url.searchParams.getAll('response_type').length === 1 && url.searchParams.get('response_type') === 'code'
      && url.searchParams.getAll('redirect_uri').length === 1 && redirect.protocol === 'https:'
      && !redirect.username && !redirect.password && !redirect.search && !redirect.hash
      && redirect.pathname === `/auth/social/${provider}/callback`;
  } catch { return false; }
}
